import React, { createContext, useState, useEffect } from "react";
import {
  login as apiLogin,
  logout as apiLogout,
  forgotPassword as apiForgotPassword,
  changePassword as apiChangePassword, // Import changePassword API
} from "../api/authService";
import jwtDecode from "jwt-decode";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState({
    accessToken: null,
    idToken: null,
    refreshToken: null,
  });
  const [loading, setLoading] = useState(true); // Add loading state

  // Save tokens to local storage
  const saveTokens = (accessToken, idToken, refreshToken) => {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("idToken", idToken);
    localStorage.setItem("refreshToken", refreshToken);
  };

  // Load tokens from local storage
  const loadTokens = () => ({
    accessToken: localStorage.getItem("accessToken"),
    idToken: localStorage.getItem("idToken"),
    refreshToken: localStorage.getItem("refreshToken"),
  });

  // Clear tokens from local storage
  const clearTokens = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("refreshToken");
  };

  // Decode idToken to extract user info
  const decodeIdToken = (idToken) => {
    try {
      const decodedToken = jwtDecode(idToken);
      const username = decodedToken["cognito:username"] || "User";
      const groups = decodedToken["cognito:groups"] || [];
      return { username, groups };
    } catch (error) {
      console.error("Failed to decode idToken:", error);
      return { username: null, groups: [] };
    }
  };

  // Login function
  const login = async (credentials) => {
    try {
      const response = await apiLogin(credentials);
      const { accessToken, idToken, refreshToken } = response.data;

      saveTokens(accessToken, idToken, refreshToken);
      setTokens({ accessToken, idToken, refreshToken });

      // Decode idToken to set user info
      const { username, groups } = decodeIdToken(idToken);
      setUser({ username, groups });
    } catch (err) {
      // Extract error status and message
      const status = err.response?.status;
      const errorMessage = err.response?.data?.errorMessage || "Login failed";
      throw { status, errorMessage }; // Throw an object with status and message
    }
  };

  // Forgot Password function
  const forgotPassword = async (username) => {
    try {
      await apiForgotPassword({ username }); // Call forgotPassword API
    } catch (err) {
      throw new Error(err.response?.data?.message || "Failed to send reset instructions");
    }
  };

  // Change Password function
  const changePassword = async ({ username, verificationCode, newPassword }) => {
    try {
      await apiChangePassword({ username, verificationCode, newPassword }); // Call changePassword API
    } catch (err) {
      throw new Error(err.response?.data?.message || "Failed to change password");
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Make API call to logout the user
      await apiLogout({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });

      // Clear local tokens and user data
      clearTokens();
      setTokens({ accessToken: null, idToken: null, refreshToken: null });
      setUser(null);
    } catch (err) {
      console.error("Failed to logout:", err.response?.data?.message || err.message);
      throw new Error(err.response?.data?.message || "Logout failed");
    }
  };


  // Restore session on app load
  useEffect(() => {
    const storedTokens = loadTokens();
    if (storedTokens.idToken) {
      try {
        // Decode idToken to restore user info
        const { username, groups } = decodeIdToken(storedTokens.idToken);

        setTokens(storedTokens);
        setUser({ username, groups });
      } catch (error) {
        console.warn("Failed to restore session:", error);
        clearTokens();
      }
    }
    setLoading(false); // Session restoration is complete
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        loading,
        login,
        forgotPassword,
        changePassword, // Expose changePassword in context
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
